import { useEffect, useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, Users, Search } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { Textarea } from '@/components/ui/Textarea'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Toggle } from '@/components/ui/Toggle'
import { Modal } from '@/components/ui/Modal'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import {
  ServicesService,
  type Service,
  type ServiceWritePayload,
} from '@/services/modules/ServicesService'
import {
  ServiceProvidersService,
  type ServiceProvider,
} from '@/services/modules/ServiceProvidersService'
import { toast } from '@/components/ui/Toast'
import { normalizeForMatch } from '@/lib/validators'
import type { PaginationMeta } from '@/types/api'
import { type PageSize } from '@/hooks/useCrudList'
import { useAuthority } from '@/hooks/useAuthority'

/**
 * Catálogo de Serviços (Consultas & Exames, Odontologia, Funeral). Cada
 * serviço carrega N prestadores habilitados (multi-select via chips).
 *
 * Modal por simplicidade — o form tem só nome + status + lista de chips
 * dos prestadores cadastrados.
 */
export function SettingsServicesPage() {
  const [reload, setReload] = useState(0)
  const { can } = useAuthority()
  const canCreate = can('settings.services.create')
  const canEdit = can('settings.services.edit')
  const canDelete = can('settings.services.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar serviço' })
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [rows, setRows] = useState<Service[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: Service }>({ open: false })
  const [openDelete, setOpenDelete] = useState<Service | null>(null)
  const [providers, setProviders] = useState<ServiceProvider[]>([])

  useEffect(() => {
    let active = true
    setLoading(true)
    ServicesService.list({ page, size, ...sf.filters })
      .then(res => {
        if (!active) return
        setRows(res.data)
        setMeta(res.meta)
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [page, size, sf.filters, reload])

  useEffect(() => {
    // carrega prestadores ATIVOs para o multi-select do form
    ServiceProvidersService.list({ size: 100, status: 'ATIVO' }).then(r => setProviders(r.data))
  }, [reload])

  const exp = useDataTableExport({
    filename: 'servicos',
    service: ServicesService,
    filters: sf.filters,
    columns: [
      { header: 'Serviço',     accessor: (s: Service) => s.name },
      { header: 'Prestadores', accessor: (s: Service) => (s.providers ?? []).map(p => p.name).join(', ') },
      { header: 'Status',      accessor: (s: Service) => s.status },
    ],
  })

  async function handleSave(payload: ServiceWritePayload) {
    try {
      if (openForm.item) {
        await ServicesService.update(openForm.item.id, payload)
        toast.success({ title: 'Serviço atualizado', description: payload.name ?? '' })
      } else {
        await ServicesService.create(payload)
        toast.success({ title: 'Serviço criado', description: payload.name ?? '' })
      }
      setOpenForm({ open: false })
      setReload(v => v + 1)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.'
      toast.error({ title: 'Não foi possível salvar', description: msg })
    }
  }

  async function handleDelete() {
    if (!openDelete) return
    try {
      await ServicesService.destroy(openDelete.id)
      toast.success({ title: 'Serviço removido', description: openDelete.name })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao remover.'
      toast.error({ title: 'Não foi possível remover', description: msg })
    }
  }

  const columns: DataTableColumn<Service>[] = [
    {
      key: 'name',
      header: 'Serviço',
      sortable: true,
      sortKey: 'name',
      alwaysVisible: true,
      render: s => <span className="font-medium text-gray-800">{s.name}</span>,
    },
    {
      key: 'providers',
      header: 'Prestadores',
      align: 'center',
      className: 'w-36',
      render: s => {
        const list = s.providers ?? []
        if (list.length === 0) return <span className="text-xs text-gray-400">—</span>
        return (
          <InfoPopover
            ariaLabel={`${list.length} prestadores`}
            title="Prestadores habilitados"
            trigger={
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200">
                <Users size={12} />
                {list.length}
              </span>
            }
          >
            <ul className="space-y-1 text-sm">
              {list.map(p => (
                <li key={p.id} className="text-gray-700">{p.name}</li>
              ))}
            </ul>
          </InfoPopover>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      className: 'w-24',
      render: s => <StatusDot value={s.status} />,
    },
  ]

  return (
    <>
      <PageHeader
        title="Serviços"
        subtitle="O que está incluso (Consultas, Odontologia, Funeral) e quem pode prestar"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Serviços' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo serviço</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.services"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={s => s.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(s) => [
          ...(canEdit ? [{ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: s }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(s) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ServiceForm
        open={openForm.open}
        item={openForm.item}
        providers={providers}
        onClose={() => setOpenForm({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover serviço?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function ServiceForm({ open, item, providers, onClose, onSave }: {
  open: boolean
  item?: Service
  providers: ServiceProvider[]
  onClose: () => void
  onSave: (payload: ServiceWritePayload) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sortOrder, setSortOrder] = useState<number | null>(0)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(item?.name ?? '')
    setDescription(item?.description ?? '')
    setSortOrder(item?.sortOrder ?? 0)
    setStatus(item?.status ?? 'ATIVO')
    setSelectedIds(new Set((item?.providers ?? []).map(p => p.id)))
    setSearch('')
  }, [open, item])

  function toggle(id: string, next: boolean) {
    setSelectedIds(prev => {
      const set = new Set(prev)
      if (next) set.add(id); else set.delete(id)
      return set
    })
  }

  // Lista filtrada por busca (case+accent insensitive — regra global).
  const filteredProviders = useMemo(() => {
    const q = normalizeForMatch(search)
    if (!q) return providers
    return providers.filter(p => normalizeForMatch(p.name).includes(q))
  }, [providers, search])

  // Estado dos "Selecionar todos" considera só a lista filtrada
  const allFilteredSelected = filteredProviders.length > 0
    && filteredProviders.every(p => selectedIds.has(p.id))
  const someFilteredSelected = filteredProviders.some(p => selectedIds.has(p.id))

  function toggleAllFiltered() {
    const next = new Set(selectedIds)
    if (allFilteredSelected) {
      filteredProviders.forEach(p => next.delete(p.id))
    } else {
      filteredProviders.forEach(p => next.add(p.id))
    }
    setSelectedIds(next)
  }

  async function submit() {
    setSaving(true)
    try {
      await onSave({
        name,
        description: description.trim() || null,
        sort_order: sortOrder ?? 0,
        status,
        provider_ids: Array.from(selectedIds),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={item ? 'Editar serviço' : 'Novo serviço'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={saving} disabled={!name.trim()}>Salvar</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
          <Input
            label="Nome"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Consultas & Exames"
          />
          <NumberInput
            label="Ordem"
            value={sortOrder}
            onChange={setSortOrder}
            min={0}
            placeholder="Ex.: 1"
            hint="Ordem no PDF"
          />
        </div>

        <Textarea
          label="Descrição (proposta)"
          rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Texto do benefício exibido na proposta comercial"
        />

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Prestadores habilitados
            </label>
            <span className="text-xs text-gray-500">
              {selectedIds.size} de {providers.length} selecionado{selectedIds.size === 1 ? '' : 's'}
            </span>
          </div>
          <p className="mb-2 text-xs text-gray-500">
            Quem pode prestar este serviço. Ao montar um produto, só os marcados aqui aparecerão como opção.
          </p>

          {providers.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-xs text-gray-500">
              Nenhum prestador cadastrado ainda.
            </div>
          ) : (
            <div className="rounded-md border border-gray-200">
              {/* Toolbar: busca + selecionar/desmarcar todos */}
              <div className="flex items-center gap-2 border-b border-gray-200 p-2">
                <div className="relative flex-1">
                  <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar prestador"
                    className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-7 pr-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={toggleAllFiltered}
                  className="cursor-pointer rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {allFilteredSelected ? 'Desmarcar todos' : 'Marcar todos'}
                </button>
              </div>

              {/* Lista de toggles (1/linha) com scroll */}
              <div className="max-h-64 overflow-y-auto">
                {filteredProviders.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-400">
                    Nenhum prestador corresponde à busca.
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filteredProviders.map(p => (
                      <li key={p.id} className="flex items-center justify-between px-3 py-2">
                        <span className="text-sm text-gray-700">{p.name}</span>
                        <Toggle
                          checked={selectedIds.has(p.id)}
                          onChange={(v) => toggle(p.id, v)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {/* Hint usado para evitar warning unused */}
          {someFilteredSelected && <span className="hidden" />}
        </div>

        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
